// Types
import type { components } from '@/types/schema'

// Redux
import { createSlice } from '@reduxjs/toolkit'

export type TSettings = {
  addresses: components['schemas']['Address'][]
  clients: components['schemas']['Client'][]
  employees: components['schemas']['Employee'][]
  // subcontractors: TSubContractor[]
  suppliers: components['schemas']['Supplier'][]
  vatRates: components['schemas']['VATRate'][]
  projectTypes: components['schemas']['ProjectType'][]
  accounts: components['schemas']['XeroAccount'][]
}

export type TSettingsPayload = {
  addresses?: components['schemas']['Address'][]
  clients?: components['schemas']['Client'][]
  employees?: components['schemas']['Employee'][]
  // subcontractors?: TSubContractor[]
  suppliers?: components['schemas']['Supplier'][]
  vatRates?: components['schemas']['VATRate'][]
  projectTypes?: components['schemas']['ProjectType'][]
  accounts?: components['schemas']['XeroAccount'][]
}

const initialState: TSettings = {
  addresses: [],
  clients: [],
  employees: [],
  // subcontractors: [],
  suppliers: [],
  vatRates: [],
  projectTypes: [],
  accounts: [],
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    addAddress(state, action: { payload: components['schemas']['Address'] }) {
      state.addresses.push(action.payload)
    },
    addAccount(state, action: { payload: components['schemas']['XeroAccount'] }) {
      state.accounts.push(action.payload)
    },
    addClient(state, action: { payload: components['schemas']['Client'] }) {
      state.clients.push(action.payload)
    },
    addEmployee(state, action: { payload: components['schemas']['Employee'] }) {
      state.employees.push(action.payload)
    },
    addProjectType(state, action: { payload: components['schemas']['ProjectType'] }) {
      state.projectTypes.push(action.payload)
    },
    addSupplier(state, action: { payload: components['schemas']['Supplier'] }) {
      state.suppliers.push(action.payload)
    },
    addVatRate(state, action: { payload: components['schemas']['VATRate'] }) {
      state.vatRates.push(action.payload)
    },
    addRegion(state, action: { payload: components['schemas']['Region'] }) {
      state.region.push(action.payload)
    },
    deleteAddress(state, action) {
      state.addresses = state.addresses.filter((address) => address.id !== action.payload)
    },
    deleteAccount(state, action) {
      state.accounts = state.accounts.filter((account) => account.id !== action.payload)
    },
    deleteClient(state, action) {
      state.clients = state.clients.filter((client) => client.id !== action.payload)
    },
    deleteEmployee(state, action) {
      state.employees = state.employees.filter((employee) => +employee.id !== action.payload)
    },
    deleteProjectType(state, action) {
      state.projectTypes = state.projectTypes.filter((projectType) => projectType.id !== action.payload)
    },
    deleteSupplier(state, action) {
      state.suppliers = state.suppliers.filter((supplier) => supplier.id !== action.payload)
    },
    deleteVatRate(state, action) {
      state.vatRates = state.vatRates.filter((vatRate) => vatRate.id !== action.payload)
    },
    setAddresses(state, action: { payload: components['schemas']['Address'][] }) {
      state.addresses = action.payload
    },
    setAccounts(state, action: { payload: components['schemas']['XeroAccount'][] }) {
      state.accounts = action.payload
    },
    setRegion(state, action: { payload: components['schemas']['Region'][] }) {
      state.region = action.payload
    },
    setClients(state, action: { payload: components['schemas']['Client'][] }) {
      state.clients = action.payload
    },
    setEmployees(state, action: { payload: components['schemas']['Employee'][] }) {
      state.employees = action.payload
    },
    setProjectTypes(state, action: { payload: components['schemas']['ProjectType'][] }) {
      state.projectTypes = action.payload
    },
    setSettings(state, action: { payload: TSettingsPayload }) {
      const { addresses, accounts, clients, employees, suppliers, vatRates, projectTypes, region } = action.payload

      if (addresses) state.addresses = addresses
      if (accounts) state.accounts = accounts
      if (clients) state.clients = clients
      if (employees) state.employees = employees
      if (suppliers) state.suppliers = suppliers
      if (vatRates) state.vatRates = vatRates
      if (projectTypes) state.projectTypes = projectTypes
      if (region) state.region = region
    },
    setSuppliers(state, action: { payload: components['schemas']['Supplier'][] }) {
      state.suppliers = action.payload
    },
    setVatRates(state, action: { payload: components['schemas']['VATRate'][] }) {
      state.vatRates = action.payload
    },
    updateAddress(state, action: { payload: components['schemas']['Address'] }) {
      const index = state.addresses.findIndex((address) => address.id === action.payload.id)
      state.addresses[index] = action.payload
    },
    updateAccount(state, action: { payload: components['schemas']['XeroAccount'] }) {
      const index = state.accounts.findIndex((account) => account.id === action.payload.id)
      state.accounts[index] = action.payload
    },
    updateRegion(state, action: { payload: components['schemas']['Region'] }) {
      const index = state.region.findIndex((region) => region.id === action.payload.id)
      state.region[index] = action.payload
    },
    updateClient(state, action: { payload: components['schemas']['Client'] }) {
      const index = state.clients.findIndex((client) => client.id === action.payload.id)
      state.clients[index] = action.payload
    },
    updateEmployee(state, action: { payload: components['schemas']['Employee'] }) {
      const index = state.employees.findIndex((employee) => employee.id === action.payload.id)
      state.employees[index] = action.payload
    },
    updateProjectType(state, action: { payload: components['schemas']['ProjectType'] }) {
      const index = state.projectTypes.findIndex((projectType) => projectType.id === action.payload.id)
      state.projectTypes[index] = action.payload
    },
    updateSupplier(state, action: { payload: components['schemas']['Supplier'] }) {
      const index = state.suppliers.findIndex((supplier) => supplier.id === action.payload.id)
      state.suppliers[index] = action.payload
    },
    updateVatRate(state, action: { payload: components['schemas']['VATRate'] }) {
      const index = state.vatRates.findIndex((vatRate) => vatRate.id === action.payload.id)
      state.vatRates[index] = action.payload
    },
  },
})

export type TSettingsState = TSettings

export default settingsSlice.reducer
export const {
  setSettings,
  setAddresses,
  setAccounts,
  setClients,
  setEmployees,
  setProjectTypes,
  setRegion,
  // setSubcontractors,
  setSuppliers,
  setVatRates,
  addAddress,
  addAccount,
  addClient,
  addEmployee,
  addProjectType,
  // addSubcontractor,
  addSupplier,
  addVatRate,
  addRegion,
  updateAddress,
  updateAccount,
  updateClient,
  updateEmployee,
  updateProjectType,
  // updateSubcontractor,
  updateSupplier,
  updateVatRate,
  updateRegion,
  deleteAddress,
  deleteAccount,
  deleteClient,
  deleteEmployee,
  deleteProjectType,
  // deleteSubcontractor,
  deleteSupplier,
  deleteVatRate,
} = settingsSlice.actions
